import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import Header from '../../components/Header';
import MultiSelectBusca from '../../components/MultiSelectBusca';
import api from '../../api';

// 'todos' (ou vazio) = sem restrição -> no front vira lista vazia.
const paraLista = (csv, numerico) => {
  const v = (csv || '').trim().toLowerCase();
  if (!v || v === 'todos') return [];
  return v.split(',').map(s => (numerico ? parseInt(s.trim()) : s.trim())).filter(x => numerico ? !!x : !!x);
};
const paraCsv = (lista) => (!lista || lista.length === 0) ? 'todos' : lista.join(',');

// datetime-local precisa de 'YYYY-MM-DDTHH:mm' no fuso local
const paraInput = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt)) return '';
  return new Date(dt.getTime() - dt.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

export default function AvisoEditor() {
  const { id } = useParams();
  const editando = !!id;
  const navigate = useNavigate();

  const [opcoes, setOpcoes] = useState({ perfis: [], filiais: [] });
  const [pronto, setPronto] = useState(!editando);
  const [salvando, setSalvando] = useState(false);
  const [f, setF] = useState({
    titulo: '', conteudo: '', prioridade: 'normal',
    dataInicio: '', dataFim: '', perfis: [], filiais: [],
  });
  const set = (campo, valor) => setF(s => ({ ...s, [campo]: valor }));

  useEffect(() => {
    api.get('/avisos/opcoes')
      .then(({ data }) => setOpcoes({ perfis: data.perfis || [], filiais: data.filiais || [] }))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!editando) return;
    api.get(`/avisos/${id}`).then(({ data }) => {
      const a = data.aviso;
      setF({
        titulo: a.titulo || '', conteudo: a.conteudo || '', prioridade: a.prioridade || 'normal',
        dataInicio: paraInput(a.data_inicio), dataFim: paraInput(a.data_fim),
        perfis: paraLista(a.perfis_alvo, true), filiais: paraLista(a.filiais_alvo, false),
      });
      setPronto(true);
    }).catch(() => {
      Swal.fire('Erro', 'Não foi possível carregar o aviso.', 'error');
      navigate('/admin/avisos');
    });
  }, [id, editando, navigate]);

  async function onSubmit(e) {
    e.preventDefault();
    if (!f.titulo.trim()) return Swal.fire('Falta o título', 'Dê um título ao aviso.', 'warning');
    setSalvando(true);
    const payload = {
      titulo: f.titulo.trim(),
      conteudo: f.conteudo.trim() || null,
      prioridade: f.prioridade,
      data_inicio: f.dataInicio || null,
      data_fim: f.dataFim || null,
      perfis_alvo: paraCsv(f.perfis),
      filiais_alvo: paraCsv(f.filiais),
    };
    try {
      if (editando) await api.put(`/avisos/${id}`, payload);
      else await api.post('/avisos', payload);
      await Swal.fire({
        icon: 'success', title: editando ? 'Aviso atualizado!' : 'Aviso publicado!',
        timer: 1400, showConfirmButton: false,
      });
      navigate('/admin/avisos');
    } catch (err) {
      Swal.fire('Erro', err.response?.data?.error || 'Não foi possível salvar.', 'error');
    } finally { setSalvando(false); }
  }

  const perfisOpts = opcoes.perfis.map(p => ({ value: p.id, label: p.titulo }));
  const filiaisOpts = opcoes.filiais.map(x => ({ value: x.codigo, label: x.nome }));

  return (
    <>
      <Header />
      <main className="conteudo-container" style={{ maxWidth: 860 }}>
        <Link to="/admin/avisos" className="doc-voltar"><i className="bi bi-arrow-left"></i> Central de Avisos</Link>
        <h1 style={{ color: 'var(--verde)', fontWeight: 700, marginBottom: 16 }}>
          {editando ? 'Editar aviso' : 'Novo aviso'}
        </h1>

        {!pronto ? <div className="conteudo-card">Carregando…</div> : (
        <form className="conteudo-card doc-form" onSubmit={onSubmit}>
          <label className="doc-label">Título *</label>
          <input className="doc-input" value={f.titulo} onChange={e => set('titulo', e.target.value)} maxLength={200} autoFocus />

          <label className="doc-label">Conteúdo</label>
          <textarea className="doc-input" value={f.conteudo} onChange={e => set('conteudo', e.target.value)}
                    rows={6} placeholder="Texto do aviso (quebras de linha são preservadas)…" />

          <div className="doc-form-linha">
            <div style={{ flex: 1 }}>
              <label className="doc-label">Prioridade</label>
              <select className="doc-input" value={f.prioridade} onChange={e => set('prioridade', e.target.value)}>
                <option value="baixa">Baixa</option>
                <option value="normal">Normal</option>
                <option value="alta">Alta</option>
                <option value="urgente">Urgente</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label className="doc-label">Começa a aparecer em</label>
              <input type="datetime-local" className="doc-input" value={f.dataInicio} onChange={e => set('dataInicio', e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label className="doc-label">Sai do ar em</label>
              <input type="datetime-local" className="doc-input" value={f.dataFim} onChange={e => set('dataFim', e.target.value)} />
            </div>
          </div>
          <p className="doc-hint">Datas em branco = aparece imediatamente e fica até você tirar.</p>

          <label className="doc-label" style={{ marginTop: 12 }}>Quem vê — cargos</label>
          <MultiSelectBusca options={perfisOpts} values={f.perfis} onChange={v => set('perfis', v)}
                            placeholder="Buscar cargo…" icon="bi-people" />
          {f.perfis.length === 0 && <div className="doc-perfis-vazio">Todos os cargos.</div>}

          <label className="doc-label" style={{ marginTop: 12 }}>Quem vê — filiais</label>
          <MultiSelectBusca options={filiaisOpts} values={f.filiais} onChange={v => set('filiais', v)}
                            placeholder="Buscar filial…" icon="bi-shop" />
          {f.filiais.length === 0 && <div className="doc-perfis-vazio">Todas as filiais.</div>}

          <div className="doc-form-acoes">
            <button type="submit" className="btn-centrao" disabled={salvando}>
              {salvando ? 'Salvando…' : (editando ? 'Salvar alterações' : 'Publicar aviso')}
            </button>
            <Link to="/admin/avisos" className="btn btn-outline-secondary">Cancelar</Link>
          </div>
        </form>
        )}
      </main>
    </>
  );
}
